import {
  VISIBILITY_LABELS,
  VISIBILITY_OPTIONS
} from 'domains/IngredientsTree/constants'
import {
  addNodeToTree,
  createNode,
  getParentPath,
  updateNode,
  updateNodeInTree
} from 'domains/IngredientsTree/helpers'
import { useCallback, useState } from 'react'

import IngredientTree from '../models/Ingredient'
import { message } from 'antd'
import { useIngredientContext } from 'contexts/Ingredient/IngredientProvider'
import { useTranslations } from 'contexts/Translation'
import { useUser } from 'domains/User/context'

const normalizeVisibility = (value) => {
  if (!value) return null

  // already canonical
  if (Object.values(VISIBILITY_OPTIONS).includes(value)) return value

  // old label -> canonical
  if (VISIBILITY_LABELS[value]) return value
  const byLabel = Object.entries(VISIBILITY_LABELS).find(
    ([, label]) => label === value
  )
  if (byLabel) return byLabel[0]

  // flexible fallback: "Fully visible" / "fully visible" / "FULLY VISIBLE"
  const normalized = String(value).trim().toLowerCase().replace(/\s+/g, ' ')
  if (normalized === 'fully visible') return VISIBILITY_OPTIONS.FULLY_VISIBLE
  if (normalized === 'partially visible')
    return VISIBILITY_OPTIONS.PARTIALLY_VISIBLE
  if (normalized === 'fully hidden') return VISIBILITY_OPTIONS.FULLY_HIDDEN

  return null
}

/**
 * Custom hook for handling actions related to the advanced form of an ingredient.
 * Supports both CREATE and UPDATE operations.
 * For CREATE: Implements optimistic updates: closes modal immediately, shows node in tree with loading state,
 * then syncs with backend. On error, rolls back optimistic changes.
 * For UPDATE: Uses backend API with proper loading state and error handling.
 *
 * @param {Object} options - The options for the hook.
 * @param {Object} options.initialData - The initial data for the form (if provided, operation is UPDATE).
 * @param {Object} options.form - The form instance.
 * @param {Function} options.handleClose - Function to close the modal.
 * @param {Function} options.onUpdateCurrentTreeLevel - Function to update tree level after successful save.
 * @param {Object} options.selectedIngredient - The selected ingredient (parent for new node).
 * @param {boolean} options.isChildrenCreated - Whether creating as child of selected ingredient.
 * @returns {Object} - An object containing the available actions and state variables.
 */
const useActionsIngredientsTreeAdvancedForm = ({
  initialData,
  form,
  handleClose,
  onUpdateCurrentTreeLevel,
  selectedIngredient,
  isChildrenCreated,
  existingNodeId,
  setExistingNodeId,
  initialProductIds = []
} = {}) => {
  // [COMPONENT_STATE_HOOKS]
  const [loading, setLoading] = useState(false)

  // [ADDITIONAL_HOOKS]
  const { t } = useTranslations()
  const { user } = useUser()
  const { ingredientsTree, dispatch } = useIngredientContext()

  // Determine if this is an update operation
  // Update: when initialData has _id AND the parent path matches selectedIngredient
  // Create (copy): when initialData has _id BUT parent path doesn't match (copying to different location)
  // Create (new): when initialData doesn't have _id
  const isUpdate = !!initialData?._id && initialData?._path

  /**
   * Helper function to prepare common node data fields.
   * Used by both create and update operations.
   */
  const prepareCommonNodeData = useCallback(
    (ingredient) => {
      return {
        name: ingredient?.nameNO,
        translations: {
          EN: ingredient?.nameEN ?? null,
          NO: ingredient?.nameNO ?? null,
          UK: ingredient?.nameUK ?? null
        },
        description: ingredient?.description ?? null,
        synonyms: ingredient?.synonyms ?? [],
        hiddenSynonyms: ingredient?.hiddenSynonyms ?? [],
        eNumber: ingredient?.eNumber ? `E${ingredient?.eNumber?.trim()}` : null,
        productsIds: Array.isArray(ingredient?.productsIds)
          ? ingredient?.productsIds
          : [],
        isIngredient: ingredient?.isIngredient ?? false,
        isDeclared: ingredient?.isDeclared ?? false,
        isVisibleInRecipes: ingredient?.isVisibleInRecipes ?? false,
        visibility:
          normalizeVisibility(ingredient?.visibility) ??
          VISIBILITY_OPTIONS.FULLY_VISIBLE,
        userId: user?._id
      }
    },
    [user?._id]
  )

  /**
   * Handles CREATE operation with optimistic updates.
   * Flow:
   * 1. Close modal immediately
   * 2. Add node optimistically to context
   * 3. Show loading state
   * 4. Call backend API
   * 5. On success: sync with RDB (onUpdateCurrentTreeLevel will fetch real data)
   * 6. On error: rollback optimistic changes and sync with RDB
   */
  const handleCreateNode = useCallback(
    async (ingredient, callback) => {
      // Step 1: Prepare node data
      const nodeData = {
        ...prepareCommonNodeData(ingredient),
        parentPath:
          selectedIngredient && selectedIngredient?._path
            ? selectedIngredient?._path
            : null,
        _id: existingNodeId || null
      }
      // Step 2: Close modal immediately
      handleClose?.()
      form.resetFields()

      // Step 3: Add node optimistically to context
      if (ingredientsTree) {
        try {
          const optimisticTree = addNodeToTree(ingredientsTree, nodeData)
          dispatch({ type: 'SET_OPTIMISTIC_TREE', payload: optimisticTree })
        } catch (error) {
          console.error('Failed to add node optimistically', error)
          // Continue anyway - will sync from RDB
        }
      }

      // Step 4: Set loading state
      setLoading(true)
      dispatch({ type: 'SET_LOADING', payload: true })

      // Step 5: Call backend API
      try {
        const result = await createNode(nodeData)

        // Step 6: On success - sync with RDB
        // onUpdateCurrentTreeLevel will fetch real data from RDB and update context
        // Clear optimistic tree first, then sync - this ensures we show real data
        dispatch({ type: 'CLEAR_OPTIMISTIC_TREE' })
        await onUpdateCurrentTreeLevel(result.node, null, isChildrenCreated)

        // Step 7: Show appropriate message based on products sync status
        if (result.code === 'OK' && result.productsSync.fullyUpdated) {
          // Full success - all products updated
          message.success(t('Ingredient successfully created'))
        } else if (result.code === 'NODE_CREATED_PRODUCTS_PARTIALLY_UPDATED') {
          // Node created but products partially updated
          const failedCount = result.productsSync.failedProducts?.length || 0
          const totalPlanned = result.productsSync.totalProductsPlanned || 0
          const totalUpdated = result.productsSync.totalProductsUpdated || 0

          message.success(t('Ingredient successfully created'))
          message.warning(
            t('Some products were not updated:') +
              `${totalUpdated} of ${totalPlanned} products updated` +
              (failedCount > 0 ? ` (${failedCount} failed)` : ''),
            5 // Show for 5 seconds
          )

          // Log failed products for debugging
          if (result.productsSync.failedProducts?.length > 0) {
            console.warn('Products sync partially failed:', {
              failedProducts: result.productsSync.failedProducts,
              nodeId: result.node?._id
            })
          }
        } else {
          // Unknown status - show generic success
          message.success(t('Ingredient successfully created'))
        }

        // Final callback
        callback?.()
      } catch (error) {
        // Step 8: On error - rollback optimistic changes
        console.error('Failed to create node', error)
        dispatch({ type: 'CLEAR_OPTIMISTIC_TREE' })
        dispatch({ type: 'SET_LOADING', payload: false })

        // Sync with RDB to show actual state
        try {
          await onUpdateCurrentTreeLevel(null, null, false)
        } catch (syncError) {
          console.error('Failed to sync tree after error', syncError)
        }

        // Show appropriate error message based on error code
        if (error.code === 'TREE_UPDATE_FAILED') {
          message.error(
            t(
              'failedToUpdateIngredientTree',
              'Failed to update ingredient tree. Please try again.'
            )
          )
        } else {
          message.error(
            error.message || t('Something went wrong during data save')
          )
        }
        throw error
      } finally {
        setLoading(false)
        dispatch({ type: 'SET_LOADING', payload: false })
      }
    },
    [
      prepareCommonNodeData,
      selectedIngredient,
      handleClose,
      form,
      ingredientsTree,
      dispatch,
      onUpdateCurrentTreeLevel,
      isChildrenCreated,
      t,
      existingNodeId
    ]
  )

  /**
   * Handles UPDATE operation with optimistic updates.
   * Flow:
   * 1. Close modal immediately
   * 2. Update node optimistically in context
   * 3. Show loading state
   * 4. Call backend API
   * 5. On success: sync with RDB (onUpdateCurrentTreeLevel will fetch real data)
   * 6. On error: rollback optimistic changes and sync with RDB
   */
  const handleUpdateNode = useCallback(
    async (ingredient, callback) => {
      // Step 1: Prepare update data
      const nodeData = {
        _id: initialData?._id,
        _path: initialData?._path,
        _createdAt: initialData?._createdAt,
        _createdBy: initialData?._createdBy,
        ...prepareCommonNodeData(ingredient),
        name: ingredient?.nameNO ?? initialData?.name,
        translations: {
          EN: ingredient?.nameEN ?? initialData?.translations?.EN ?? null,
          NO: ingredient?.nameNO ?? initialData?.translations?.NO ?? null,
          UK: ingredient?.nameUK ?? initialData?.translations?.UK ?? null
        },
        eNumber: ingredient?.eNumber
          ? `E${ingredient?.eNumber?.trim()}`
          : initialData?.eNumber,
        synonyms: ingredient?.synonyms ?? initialData?.synonyms,
        hiddenSynonyms:
          ingredient?.hiddenSynonyms ?? initialData?.hiddenSynonyms,
        description: ingredient?.description ?? initialData?.description,
        productsIds: Array.isArray(ingredient?.productsIds)
          ? ingredient?.productsIds
          : initialProductIds,
        parentPath: getParentPath(initialData?._path),
        visibility:
          normalizeVisibility(ingredient?.visibility) ??
          normalizeVisibility(initialData?.visibility) ??
          VISIBILITY_OPTIONS.FULLY_VISIBLE
      }

      const oldProductsIds = Array.isArray(initialProductIds)
        ? initialProductIds
        : []

      // Step 2: Close modal immediately
      handleClose?.()
      form.resetFields()

      // Step 3: Update node optimistically in context
      if (ingredientsTree) {
        try {
          const optimisticTree = updateNodeInTree(ingredientsTree, nodeData)
          dispatch({ type: 'SET_OPTIMISTIC_TREE', payload: optimisticTree })
        } catch (error) {
          console.error('Failed to update node optimistically', error)
          // Continue anyway - will sync from RDB
        }
      }

      // Step 4: Set loading state
      setLoading(true)
      dispatch({ type: 'SET_LOADING', payload: true })

      // Step 5: Call backend API
      try {
        const result = await updateNode({ ...nodeData, oldProductsIds })

        // Step 6: On success - sync with RDB
        // onUpdateCurrentTreeLevel will fetch real data from RDB and update context
        // Clear optimistic tree first, then sync - this ensures we show real data
        dispatch({ type: 'CLEAR_OPTIMISTIC_TREE' })
        await onUpdateCurrentTreeLevel(result.node, null)

        // Step 7: Show appropriate message based on products sync status
        if (result.code === 'OK' && result.productsSync.fullyUpdated) {
          // Full success - all products updated
          message.success(t('Ingredient successfully updated'))
        } else if (result.code === 'NODE_UPDATED_PRODUCTS_PARTIALLY_UPDATED') {
          // Node updated but products partially updated
          const failedCount = result.productsSync.failedProducts?.length || 0
          const totalPlanned = result.productsSync.totalProductsPlanned || 0
          const totalUpdated = result.productsSync.totalProductsUpdated || 0

          message.success(t('Ingredient successfully updated'))
          message.warning(
            t('Some products were not updated: ') +
              `${totalUpdated} of ${totalPlanned} products updated` +
              (failedCount > 0 ? ` (${failedCount} failed)` : ''),
            5
          )

          // Log failed products for debugging
          if (result.productsSync.failedProducts?.length > 0) {
            console.warn('Products sync partially failed:', {
              failedProducts: result.productsSync.failedProducts,
              nodeId: result.node?._id
            })
          }
        } else {
          // Unknown status - show generic success
          message.success(t('Ingredient successfully updated'))
        }

        // Final callback
        callback?.()
      } catch (error) {
        // Step 8: On error - rollback optimistic changes
        console.error('Failed to update node', error)
        dispatch({ type: 'CLEAR_OPTIMISTIC_TREE' })
        dispatch({ type: 'SET_LOADING', payload: false })

        // Sync with RDB to show actual state
        try {
          await onUpdateCurrentTreeLevel(null, null, false)
        } catch (syncError) {
          console.error('Failed to sync tree after error', syncError)
        }

        // Show appropriate error message based on error code
        if (error.code === 'TREE_UPDATE_FAILED') {
          message.error(
            t(
              'failedToUpdateIngredientTree',
              'Failed to update ingredient tree. Please try again.'
            )
          )
        } else {
          message.error(
            error.message || t('Something went wrong during data save')
          )
        }
        throw error
      } finally {
        setLoading(false)
        dispatch({ type: 'SET_LOADING', payload: false })
      }
    },
    [
      initialData,
      prepareCommonNodeData,
      getParentPath,
      handleClose,
      form,
      ingredientsTree,
      dispatch,
      onUpdateCurrentTreeLevel,
      initialProductIds,
      t
    ]
  )

  /**
   * Main save form function that routes to create or update based on initialData.
   */
  const saveForm = useCallback(
    async (values, callback) => {
      const { ingredient } = values
      if (isUpdate) {
        await handleUpdateNode(ingredient, callback)
      } else {
        await handleCreateNode(ingredient, callback)
      }
    },
    [isUpdate, handleUpdateNode, handleCreateNode]
  )

  const validateForm = useCallback(
    (values) => {
      try {
        // Prepare data to be validated
        const validationData = {
          names: values?.ingredient?.names
        }
        IngredientTree.validationSchema.validateSync(validationData)
      } catch (error) {
        throw new Error(t('Validation error: ') + error.message)
      }
    },
    [t]
  )

  const onFinish = useCallback(async () => {
    if (loading) return // Avoid multiple calls

    try {
      // Get form values
      const formValues = form.getFieldsValue()

      // Validate fields
      validateForm(formValues)

      // Save data (callback is handled inside saveForm)
      await saveForm(formValues, () => {
        // Additional cleanup if needed
      })
    } catch (error) {
      // Error is already handled in saveForm
      // Just log for debugging
      console.error('onFinish error:', error)
    }
  }, [loading, form, validateForm, saveForm])

  const onReset = useCallback(() => {
    handleClose()
    form.resetFields()
    setExistingNodeId(null)
  }, [handleClose, form])

  return { onFinish, onReset, loading, saveForm }
}

export default useActionsIngredientsTreeAdvancedForm
